import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { fmtDateTimeIT, fmtDateIT, todayISO } from "@/lib/format";
import { Plus, CheckCircle2, Trash2, Clock, CalendarClock, Calendar, User, Bell, AlertCircle, ChevronRight } from "lucide-react";
import { ReminderDialog } from "@/components/ReminderDialog";
import WaOutboxPanel from "@/components/WaOutboxPanel";
import TodoDayPanel from "@/components/todos/TodoDayPanel";
import { toast } from "sonner";

const SECTION_COLORS = {
  overdue:  { name: "Scaduti",            border: "#DC2626", bg: "rgba(220,38,38,0.06)",     chip: "rgba(220,38,38,0.15)",     text: "#991B1B", icon: AlertCircle },
  today:    { name: "Oggi",               border: "#B7955B", bg: "linear-gradient(135deg, rgba(183,149,91,0.08), rgba(212,171,110,0.04))", chip: "rgba(183,149,91,0.18)", text: "#8a6e3d", icon: CalendarClock },
  next7:    { name: "Prossimi 7 giorni",  border: "#0A192F", bg: "rgba(10,25,47,0.04)",       chip: "rgba(10,25,47,0.1)",       text: "#0A192F", icon: Calendar },
  future:   { name: "Più avanti",         border: "#64748B", bg: "rgba(100,116,139,0.05)",    chip: "rgba(100,116,139,0.15)",   text: "#475569", icon: Calendar },
  done:     { name: "Completati",         border: "#16A34A", bg: "rgba(22,163,74,0.04)",      chip: "rgba(22,163,74,0.15)",     text: "#15803D", icon: CheckCircle2 },
};

function Section({ kind, count, children }) {
  const cfg = SECTION_COLORS[kind] || SECTION_COLORS.future;
  const Icon = cfg.icon;
  if (count === 0) return null;
  return (
    <div
      className="bg-white shadow-[0_1px_3px_rgba(10,25,47,0.05)] overflow-hidden"
      style={{ border: "1px solid #E8ECF2", borderRadius: 10, borderTop: `3px solid ${cfg.border}` }}
      data-testid={`section-${kind}`}
    >
      <div className="px-4 py-3 flex items-center gap-2.5" style={{ background: cfg.bg }}>
        <Icon className="w-4 h-4" style={{ color: cfg.border }} strokeWidth={2.2} />
        <h3 className="font-serif text-[15px] font-medium flex-1" style={{ color: cfg.text }}>{cfg.name}</h3>
        <span
          className="text-[10px] font-bold tabular-nums px-2 py-0.5 rounded-full"
          style={{ background: cfg.chip, color: cfg.text }}
        >
          {count}
        </span>
      </div>
      <div className="divide-y divide-[#F0F2F5]">{children}</div>
    </div>
  );
}

export default function AgendaPage() {
  const [items, setItems] = useState([]);
  const [showRem, setShowRem] = useState(false);
  const [showAppt, setShowAppt] = useState(false);
  const [editing, setEditing] = useState(null);
  // Filtro ownership per appuntamenti condivisi: "all" | "mine" | "others"
  const [ownerFilter, setOwnerFilter] = useState(() => {
    try { return localStorage.getItem("ba-agenda-owner") || "all"; } catch { return "all"; }
  });
  useEffect(() => { try { localStorage.setItem("ba-agenda-owner", ownerFilter); } catch {} }, [ownerFilter]);

  const load = useCallback(async () => {
    const r = await api.get("/reminders", { params: { limit: 1000 } });
    setItems(r.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Conteggi per il segmented control
  const counts = {
    all: items.length,
    mine: items.filter((r) => r.isMine !== false).length,
    others: items.filter((r) => r.appointment && r.isMine === false).length,
  };

  // Applica filtro ownership: i promemoria PERSONALI restano sempre visibili
  // (sono solo miei lato backend); il filtro agisce sugli APPUNTAMENTI condivisi.
  const filtered = items.filter((r) => {
    if (ownerFilter === "all") return true;
    if (ownerFilter === "mine") return r.isMine !== false;
    if (ownerFilter === "others") return r.appointment && r.isMine === false;
    return true;
  });

  const today = todayISO();
  const in7 = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);

  const overdue = filtered.filter((r) => !r.completed && r.when && r.when.slice(0, 10) < today);
  const ofToday = filtered.filter((r) => !r.completed && r.when && r.when.slice(0, 10) === today);
  const next7 = filtered.filter((r) => !r.completed && r.when && r.when.slice(0, 10) > today && r.when.slice(0, 10) <= in7);
  const future = filtered.filter((r) => !r.completed && r.when && r.when.slice(0, 10) > in7);
  const done = filtered.filter((r) => r.completed).slice(0, 20);

  const complete = async (id) => { await api.patch(`/reminders/${id}`, { completed: true }); load(); toast.success("Completato"); };
  const del = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/reminders/${id}`); load(); };
  const openEdit = (r) => {
    setEditing(r);
    if (r.appointment) setShowAppt(true);
    else setShowRem(true);
  };

  // Cache nomi clienti per render-time lookup (lazy)
  const [clientNames, setClientNames] = useState({});
  useEffect(() => {
    const missing = Array.from(new Set(items.filter((r) => r.clientId && !clientNames[r.clientId]).map((r) => r.clientId)));
    if (missing.length === 0) return;
    (async () => {
      const map = {};
      // Carica fino a 50 alla volta in parallelo
      await Promise.all(missing.slice(0, 50).map(async (cid) => {
        try {
          const r = await api.get(`/clients/${cid}`);
          map[cid] = `${(r.data?.cognome || "").toUpperCase()} ${r.data?.nome || ""}`.trim() || r.data?.email || "—";
        } catch {
          map[cid] = "Cliente non trovato";
        }
      }));
      setClientNames((prev) => ({ ...prev, ...map }));
    })();
  }, [items, clientNames]);

  const Row = ({ r, accent = "#64748B" }) => {
    const isAppt = !!r.appointment;
    const clientName = r.clientId ? clientNames[r.clientId] : null;
    return (
      <div
        className="flex items-stretch gap-0 hover:bg-slate-50/80 transition-colors cursor-pointer group"
        onClick={() => openEdit(r)}
        data-testid={`rem-${r.id}`}
      >
        {/* Stripe colorata appoggiata al bordo sx */}
        <div
          className="w-1 shrink-0"
          style={{ background: isAppt ? "#B7955B" : accent }}
        />
        <div className="flex-1 min-w-0 px-4 py-3 flex items-start gap-3">
          {/* Icona type */}
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
            style={{
              background: isAppt ? "rgba(183,149,91,0.12)" : "rgba(10,25,47,0.06)",
              color: isAppt ? "#B7955B" : accent,
              border: `1px solid ${isAppt ? "rgba(183,149,91,0.25)" : "rgba(10,25,47,0.08)"}`,
            }}
          >
            {isAppt ? <CalendarClock className="w-4 h-4" strokeWidth={2.2} /> : <Bell className="w-4 h-4" strokeWidth={2.2} />}
          </div>
          {/* Contenuto */}
          <div className="flex-1 min-w-0">
            {/* Riga 1: tipo + ora + ownerName */}
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span
                className="text-[9px] font-bold uppercase tracking-[0.16em] px-1.5 py-0.5 rounded"
                style={{
                  background: isAppt ? "rgba(183,149,91,0.15)" : "rgba(10,25,47,0.08)",
                  color: isAppt ? "#8a6e3d" : "#0A192F",
                }}
              >
                {isAppt ? "Appuntamento" : "Promemoria"}
              </span>
              <span className="text-[11.5px] font-semibold tabular-nums" style={{ color: "#0A192F" }}>
                {fmtDateTimeIT(r.when)}
              </span>
              {isAppt && r.ownerName && !r.isMine && (
                <span
                  className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ml-auto"
                  style={{ background: "rgba(183,149,91,0.12)", color: "#8a6e3d", border: "1px solid rgba(183,149,91,0.3)" }}
                  title={`Creato da ${r.ownerName}`}
                  data-testid={`rem-owner-${r.id}`}
                >
                  di {r.ownerName}
                </span>
              )}
            </div>
            {/* Riga 2: titolo principale (font serif premium) */}
            <h4 className={`font-serif text-[16px] leading-tight truncate ${r.completed ? "line-through text-slate-400" : "text-slate-900"}`}>
              {r.title || (isAppt ? "Appuntamento" : "Promemoria senza titolo")}
            </h4>
            {/* Riga 3: nome cliente collegato (visibile, NON sopra il titolo) */}
            {r.clientId && (
              <Link
                to={`/clienti/${r.clientId}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 mt-1 text-[12px] font-medium hover:underline"
                style={{ color: "#B7955B" }}
                data-testid={`rem-client-${r.id}`}
              >
                <User className="w-3 h-3" strokeWidth={2.2} />
                {clientName || "Cliente collegato"}
                <ChevronRight className="w-3 h-3 opacity-60" />
              </Link>
            )}
            {/* Riga 4: note (se presenti) */}
            {r.notes && <p className="text-[12px] text-slate-600 mt-1 line-clamp-2 leading-snug">{r.notes}</p>}
          </div>
          {/* Azioni quick */}
          <div className="flex items-center gap-0.5 shrink-0 self-center opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
            {!r.completed && (
              <button
                onClick={() => complete(r.id)}
                title="Segna come fatto"
                data-testid={`rem-done-${r.id}`}
                className="p-2 hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 rounded-lg transition-colors"
              >
                <CheckCircle2 className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => del(r.id)}
              title="Elimina"
              data-testid={`rem-del-${r.id}`}
              className="p-2 hover:bg-rose-50 text-slate-400 hover:text-rose-600 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AppLayout
      title="Agenda Operativa"
      subtitle="Promemoria personali · Appuntamenti condivisi con il team"
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => setShowRem(true)} data-testid="btn-new-rem" className="text-white text-sm px-4 py-2 rounded-[3px] flex items-center gap-2" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"}><Plus className="w-4 h-4" /> Promemoria</button>
          <button onClick={() => setShowAppt(true)} data-testid="btn-new-appt" className="bg-[#B7955B] hover:bg-[#A18048] text-white text-sm px-4 py-2 rounded-[3px] flex items-center gap-2"><Calendar className="w-4 h-4" /> Appuntamento</button>
        </div>
      }
    >
      <WaOutboxPanel />
      <TodoDayPanel />
      {/* Segmented control: filtro Solo i miei / Tutti / Altri */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="agenda-owner-filter">
        <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Visualizza:</span>
        {[
          { val: "all", label: "Tutti", count: counts.all },
          { val: "mine", label: "Solo i miei", count: counts.mine },
          { val: "others", label: "Del team", count: counts.others },
        ].map((opt) => (
          <button
            key={opt.val}
            type="button"
            onClick={() => setOwnerFilter(opt.val)}
            data-testid={`agenda-filter-${opt.val}`}
            className="px-3 py-1.5 text-[12px] font-semibold rounded-full flex items-center gap-1.5 transition-all"
            style={{
              background: ownerFilter === opt.val ? "#0A192F" : "#fff",
              color: ownerFilter === opt.val ? "#fff" : "#475569",
              border: ownerFilter === opt.val ? "1px solid #0A192F" : "1px solid #E2E8F0",
              boxShadow: ownerFilter === opt.val ? "0 1px 3px rgba(10,25,47,0.18)" : "none",
            }}
          >
            {opt.label}
            <span
              className="text-[10px] tabular-nums px-1.5 py-0.5 rounded-full font-bold"
              style={{
                background: ownerFilter === opt.val ? "rgba(183,149,91,0.25)" : "rgba(148,163,184,0.18)",
                color: ownerFilter === opt.val ? "#D4AB6E" : "#475569",
                minWidth: 18,
                textAlign: "center",
              }}
            >
              {opt.count}
            </span>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Section kind="overdue" count={overdue.length}>
          {overdue.map((r) => <Row key={r.id} r={r} accent="#DC2626" />)}
        </Section>
        <Section kind="today" count={ofToday.length}>
          {ofToday.map((r) => <Row key={r.id} r={r} accent="#B7955B" />)}
        </Section>
        <Section kind="next7" count={next7.length}>
          {next7.map((r) => <Row key={r.id} r={r} accent="#0A192F" />)}
        </Section>
        <Section kind="future" count={future.length}>
          {future.map((r) => <Row key={r.id} r={r} accent="#64748B" />)}
        </Section>

        {/* Empty state se tutto vuoto */}
        {overdue.length === 0 && ofToday.length === 0 && next7.length === 0 && future.length === 0 && done.length === 0 && (
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-[10px] py-16 px-6 text-center">
            <CalendarClock className="w-12 h-12 mx-auto mb-3 text-slate-300" strokeWidth={1.5} />
            <h3 className="font-serif text-xl text-slate-900 mb-1">Agenda vuota</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto">Crea un Promemoria o un Appuntamento usando i pulsanti in alto a destra.</p>
          </div>
        )}

        <div className="lg:col-span-2">
          <Section kind="done" count={done.length}>
            {done.map((r) => <Row key={r.id} r={r} accent="#16A34A" />)}
          </Section>
        </div>
      </div>

      <ReminderDialog
        open={showRem}
        onClose={() => { setShowRem(false); setEditing(null); }}
        onSaved={() => { setShowRem(false); setEditing(null); load(); }}
        appointment={false}
        initial={editing || undefined}
      />
      <ReminderDialog
        open={showAppt}
        onClose={() => { setShowAppt(false); setEditing(null); }}
        onSaved={() => { setShowAppt(false); setEditing(null); load(); }}
        appointment={true}
        initial={editing || undefined}
      />
    </AppLayout>
  );
}
